import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import { useWeb3React } from '@web3-react/core';
import { ethers } from 'ethers';
import { Base64 } from 'js-base64'; 
import BasicInfoReviewForm from './BasicInfoReviewForm';
import KYCLevel1ReviewForm from './KYCLevel1ReviewForm';
import KYCLevel2ReviewForm from './KYCLevel2ReviewForm';
import { signMessageDialog } from '../stores/signMessageStore';
import { fetchFromIPFS } from '../utils/ipfs';
import { createKYCContract, Permission } from '../config/contracts';
import { getKYCStatusColor, getKYCStatusText } from '../utils/walletUtils';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

interface KYCStatus {
  templateId: number;
  status: number;
  submittedAt?: Date;
  ipfsHash?: string;
}

interface AssetsKycManagerProps {
  tokenId: string;
  selectedKYCTypes:number [];
  permission?:Permission;
  isWhitelisted:boolean;
  onReviewSubmit?: (templateId: number, approved: boolean, comments?: string) => Promise<void>;
  onResetSubmit?: (templateId: number) => Promise<void>; 
}

const AssetsKycManager: React.FC<AssetsKycManagerProps> = ({ tokenId,selectedKYCTypes,permission,isWhitelisted,onReviewSubmit,onResetSubmit}) => {
  const { account, provider } = useWeb3React();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [tokenOwner, setTokenOwner] = useState('');
  const [kycStatus, setKycStatus] = useState<KYCStatus[]>([]);
  const [basicInfo, setBasicInfo] = useState<any>(null);
  const [kycLevel1, setKycLevel1] = useState<any>(null);
  const [kycLevel2, setKycLevel2] = useState<any>(null);
  


  // 获取合约实例
  const getContract = () => {
    if (!provider || !account) return null;
    const chainId = provider.network?.chainId;
    if (!chainId) return null;
    const signer = provider.getSigner(account);
    return createKYCContract.writable(chainId, signer);
  };

  // 加载Token数据
  const loadTokenData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const contract = getContract();
      if (!contract) {
        throw new Error('Please connect your wallet first');
      }

      // 获取Token所有者
      const owner = await contract.ownerOf(tokenId);
      setTokenOwner(owner); 
      // 获取KYC状态
      const statusPromises = selectedKYCTypes.map(async (templateId) => {
        try {
          const kycData = await contract.getKYCData(templateId,tokenId);
            console.log("getMyKYCData:",kycData);
          return {
            templateId,
            status: kycData.status,
            submittedAt: kycData.submittedAt > 0 ? new Date(kycData.submittedAt * 1000) : undefined,
            ipfsHash: kycData.ipfsHash || undefined
          };
        } catch (err) {
          return {
            templateId,
            status: 0,
            submittedAt: undefined,
            ipfsHash: undefined
          };
        }
      });

      const statuses = await Promise.all(statusPromises);
      setKycStatus(statuses);

      // 加载KYC数据
      await loadKYCData(statuses);
      
    } catch (err: any) {
      console.error('Failed to load token data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // 加载KYC数据
  const loadKYCData = async (statuses: KYCStatus[]) => {
    try {
      // 加载基本信息 (模板0)
      const basicInfoStatus = statuses.find(s => s.templateId === 1);
      if (basicInfoStatus?.ipfsHash) {
        const basicInfoData = await fetchFromIPFS(basicInfoStatus.ipfsHash);
        setBasicInfo(basicInfoData);
      }

      // 加载KYC Level 1 (模板1)
      const level1Status = statuses.find(s => s.templateId === 2);
      if (level1Status?.ipfsHash) {
        const level1Data = await JSON.parse(Base64.decode(level1Status.ipfsHash));
        setKycLevel1(level1Data);
      }

      // 加载KYC Level 2 (模板2)
      const level2Status = statuses.find(s => s.templateId === 3);
      if (level2Status?.ipfsHash) {
        const level2Data = JSON.parse(Base64.decode(level2Status.ipfsHash));
        setKycLevel2(level2Data);
      }
    } catch (err) {
      console.error('Failed to load KYC data:', err);
    }
  };

  // 处理审核提交
  const handleReviewSubmit = async (templateId: number, approved: boolean, comments?: string) => {
    if (onReviewSubmit) {
      await onReviewSubmit(templateId, approved, comments);
      // 重新加载数据以更新状态
      // await loadTokenData();
    }
  };
  // reset审核提交
  const handleResetSubmit = async (templateId: number) => {
    if (onResetSubmit) {
      await onResetSubmit(templateId); 
    }
  };

  useEffect(() => {
    if (tokenId && provider && account) {
      loadTokenData();
    }
  }, [tokenId, provider, account]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button 
            variant="contained" 
            onClick={() => {
              setError('');
              loadTokenData();
            }}
          >
            Retry
          </Button>
        </Box>
      </Box>
    );
  }
  // console.log(selectedKYCTypes)
//   console.log(!selectedKYCTypes?.includes(1))
//  console.log(!selectedKYCTypes?.includes(2))
//   console.log(!selectedKYCTypes?.includes(3))
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          KYC Data Review
        </Typography>
        
        {/* Token信息显示 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Token Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Token ID
                  </Typography>
                  <Typography variant="h6">
                    {tokenId}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Owner Address
                  </Typography>
                  <Typography variant="h6" sx={{ wordBreak: 'break-all' }}>
                    {tokenOwner ? `${tokenOwner}` : 'Loading...'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* KYC状态显示 */}
        {kycStatus.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              KYC Status
            </Typography>
            <Grid container spacing={2}>
              {kycStatus.map((status) => (
                <Grid item xs={12} md={4} key={status.templateId}>
                  <Card>
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom>
                        KYC Template {status.templateId}
                      </Typography>
                      <Chip
                        label={getKYCStatusText(status.status)}
                        color={getKYCStatusColor(status.status)}
                        size="small"
                      />
                      {status.submittedAt && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Submission Time: {status.submittedAt.toLocaleDateString()}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* KYC审核表单Tabs */}
        <Box sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} aria-label="KYC Review Tabs">
              <Tab label="Basic Info (KYC 1)" {...a11yProps(1)} disabled={isWhitelisted&&!selectedKYCTypes?.includes(1)} />
              <Tab label="Identity Verification (KYC 2)" {...a11yProps(2)} disabled={isWhitelisted&&!selectedKYCTypes?.includes(2)}/>
              <Tab label="Video Verification (KYC 3)" {...a11yProps(3)} disabled={isWhitelisted&&!selectedKYCTypes?.includes(3)}/>
            </Tabs>
          </Box>
          
          {/* 基本信息审核 */}
          <TabPanel value={activeTab} index={0}>
            {basicInfo ? (
              <BasicInfoReviewForm
                isWhitelisted={isWhitelisted}
                data={basicInfo}
                status={kycStatus.find(s => s.templateId === 1)?.status || 0}
                onReview={(approved, comments) => handleReviewSubmit(1, approved, comments)}
                canReview={onReviewSubmit !== undefined}
                onReset={() => handleResetSubmit(1)}
                canReset={onResetSubmit !== undefined}
              />
            ) : (
              <Alert severity="info">
                This user has not yet submitted basic information
              </Alert>
            )}
          </TabPanel>
          
          {/* 身份认证审核 */}
          <TabPanel value={activeTab} index={1}>
            {kycLevel1 ? (
              <KYCLevel1ReviewForm
                 isWhitelisted={isWhitelisted}
                data={kycLevel1}
                status={kycStatus.find(s => s.templateId === 2)?.status || 0}
                onReview={(approved, comments) => handleReviewSubmit(2, approved, comments)}
                canReview={onReviewSubmit !== undefined}
                onReset={() => handleResetSubmit(2)}
                canReset={onResetSubmit !== undefined}
              />
            ) : (
              <Alert severity="info">
                This user has not yet submitted identity verification information
              </Alert>
            )}
          </TabPanel>
          
          {/* 视频认证审核 */}
          <TabPanel value={activeTab} index={2}>
            {kycLevel2 ? (
              <KYCLevel2ReviewForm
                isWhitelisted={isWhitelisted}
                data={kycLevel2}
                status={kycStatus.find(s => s.templateId === 3)?.status || 0}
                onReview={(approved, comments) => handleReviewSubmit(3, approved, comments)}
                canReview={onReviewSubmit !== undefined}
                onReset={() => handleResetSubmit(3)}
                canReset={onResetSubmit !== undefined}
              />
            ) : (
              <Alert severity="info">
                This user has not yet submitted video verification information
              </Alert>
            )}
          </TabPanel>
        </Box>
      </Paper>
      

    </Box>
  );
};

export default AssetsKycManager;